import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Helmet from "react-helmet";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import { fetchPublicTableSheet } from "../../api/tableBookingApi";
import CustomLoadingHTML from "../../components/customLoadingHTML/customLoadingHTML";
import { getCloudinaryMediaBaseUrl } from "../../config/cloudinaryBase";
import { resolveCloudinaryImageSrc } from "../../helpers/helper.service";
import {
  bookingGuestSpendLabel,
  eventStatusBadgeClass,
  formatBookingAmountDisplay,
  formatEventChipDate,
  formatEventStartDateDisplay,
  formatGeneratedAt,
  formatReleasedAt,
  getReleasedBookingsNewestFirst,
  getTableSheetSeenStorageKey,
  readSeenBookingIds,
  sectionColorClass,
  sectionHeaderLabel,
  staffReferralTitle,
  tableCodeLabel,
  buildSheetStaffNameIndex,
  pickEmployeeHoldLabel,
  tableHoldDurationLabel,
  tableHoldExpiresLabel,
  tableHoldMetaTitle,
  tableHoldRemainingLabel,
  tableHoldStatusLabel,
  tableProcessingStatusLabel,
  tableRowLabel,
  isTableCheckedIn,
  isTableOnHold,
  isTableOfflineSoldOut,
  isTableProcessing,
  normalizeTableSheetStatus,
  sheetNeedsFastPoll,
  unwrapTableSheetResponse,
  writeSeenBookingIds,
} from "../../helpers/tableSheet";
import "./publicTableSheet.css";

/** Baseline refresh while the sheet is open and idle. */
const POLL_IDLE_MS = 8_000;
/** Faster refresh during processing / holds / new bookings. */
const POLL_ACTIVE_MS = 3_000;
/** Keep fast polling briefly after the sheet payload changes. */
const POLL_BURST_MS = 12_000;

function SectionSofaIcon() {
  return (
    <svg
      className="table-sheet-section__icon"
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden
    >
      <path d="M7 13c-1.1 0-2 .9-2 2v2h16v-2c0-1.1-.9-2-2-2H7zm-2 4v2c0 .55.45 1 1 1h1v-5H6c-.55 0-1 .45-1 1zm14 0h-1v5h1c.55 0 1-.45 1-1v-2c0-.55-.45-1-1-1zM7 6c0-1.66 1.34-3 3-3h4c1.66 0 3 1.34 3 3v5H7V6zm2 0v3h6V6c0-.55-.45-1-1-1h-4c-.55 0-1 .45-1 1z" />
    </svg>
  );
}

function RowNewBadge() {
  const { t } = useTranslation();
  return (
    <span className="table-sheet-row-new-pill">{t("TABLE_SHEET_NEW_BADGE")}</span>
  );
}

function CheckedInMark() {
  const { t } = useTranslation();
  return (
    <span
      className="table-sheet-checked-in"
      title={t("TABLE_SHEET_CHECKED_IN")}
      aria-label={t("TABLE_SHEET_CHECKED_IN")}
    >
      ✓
    </span>
  );
}

function TableCodeCell({ code, checkedIn }) {
  return (
    <div className="table-sheet-row__code-wrap">
      <div className="table-sheet-row__code">{code}</div>
      {checkedIn ? <CheckedInMark /> : null}
    </div>
  );
}

function HistoryChevron({ open }) {
  return (
    <svg
      className={`table-sheet-row__chevron ${open ? "is-open" : ""}`}
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
}

function TableBookingHistoryPanel({ table }) {
  const { t } = useTranslation();
  const booking = table.booking;
  const released = getReleasedBookingsNewestFirst(table);
  const currentLabel = booking ? bookingGuestSpendLabel(booking, t) : "—";
  const staffTitle = booking ? staffReferralTitle(booking) : "";

  return (
    <div className="table-sheet-history__panel" role="list">
      <div className="table-sheet-history__item" role="listitem">
        <span className="table-sheet-history__tag">
          {t("TABLE_SHEET_CURRENT_BOOKING")}
        </span>
        <span
          className="table-sheet-history__line"
          title={staffTitle || undefined}
        >
          {currentLabel}
        </span>
      </div>
      {released.map((rb, idx) => {
        const key =
          rb?.bookingId ||
          rb?.bookingref ||
          `${rb?.releasedAt || "released"}-${idx}`;
        const releasedLabel = formatReleasedAt(rb?.releasedAt);
        return (
          <div
            key={key}
            className="table-sheet-history__item table-sheet-history__item--released"
            role="listitem"
          >
            <span className="table-sheet-history__tag table-sheet-history__tag--released">
              {t("TABLE_SHEET_RELEASED")}
            </span>
            <span className="table-sheet-history__line">
              {bookingGuestSpendLabel(rb, t)}
            </span>
            {releasedLabel ? (
              <span className="table-sheet-history__meta">
                {t("TABLE_SHEET_RELEASED_AT", { time: releasedLabel })}
              </span>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

function TableHistoryToggle({ open, onToggle, t }) {
  return (
    <button
      type="button"
      className="table-sheet-row__history-btn"
      aria-expanded={open}
      aria-label={t("TABLE_SHEET_BOOKING_HISTORY")}
      onClick={onToggle}
    >
      <HistoryChevron open={open} />
    </button>
  );
}

function TableSheetTableRow({ table, staffIndex = null }) {
  const { t } = useTranslation();
  const [historyOpen, setHistoryOpen] = useState(false);
  const code = tableCodeLabel(table);
  const booking = table.booking;
  const checkedIn = isTableCheckedIn(table);
  const status = normalizeTableSheetStatus(table.status);
  const onProcessing = isTableProcessing(table);
  const onHold = isTableOnHold(table);
  const showNew = status === "booked" && booking?.isNew === true;
  const released = getReleasedBookingsNewestFirst(table);
  const hasHistory = released.length > 0;
  const toggleHistory = () => setHistoryOpen((v) => !v);
  const historyToggle = hasHistory ? (
    <TableHistoryToggle open={historyOpen} onToggle={toggleHistory} t={t} />
  ) : null;
  const historyPanel =
    hasHistory && historyOpen ? (
      <TableBookingHistoryPanel table={table} />
    ) : null;

  // PENDING_PAYMENT → Processing only (same layout as Available / Sold out).
  if (onProcessing || status === "processing") {
    return (
      <div className="table-sheet-row-block">
        <div className="table-sheet-row table-sheet-row--processing">
          <TableCodeCell code={code} checkedIn={false} />
          <div className="table-sheet-row__detail table-sheet-row__detail--dash">
            —
            {historyToggle}
          </div>
          <div className="table-sheet-row__status table-sheet-row__status--processing">
            {tableProcessingStatusLabel(t)}
          </div>
        </div>
        {historyPanel}
      </div>
    );
  }

  // Hold / held_for_employee / held → always render as Hold + details (never raw status).
  if (onHold) {
    const holdWho = pickEmployeeHoldLabel(table, staffIndex);
    const holdTime = tableHoldRemainingLabel(table, t);
    const holdDuration = tableHoldDurationLabel(table, t);
    const holdUntil = tableHoldExpiresLabel(table, t);
    const detailTitle = tableHoldMetaTitle(table, t, staffIndex);
    const holdSecondary = [holdDuration, holdUntil].filter(Boolean).join(" · ");
    return (
      <div className="table-sheet-row-block">
        <div className="table-sheet-row table-sheet-row--hold">
          <TableCodeCell code={code} checkedIn={false} />
          <div className="table-sheet-row__detail">
            <div
              className="table-sheet-row__detail-text"
              title={detailTitle || undefined}
            >
              <div className="table-sheet-row__hold-primary">
                {holdWho ? (
                  <span className="table-sheet-row__hold-who">{holdWho}</span>
                ) : null}
                {holdWho && holdTime ? (
                  <span className="table-sheet-row__hold-sep"> · </span>
                ) : null}
                {holdTime ? (
                  <span className="table-sheet-row__hold-time">{holdTime}</span>
                ) : null}
                {!holdWho && !holdTime ? "—" : null}
              </div>
              {holdSecondary ? (
                <div className="table-sheet-row__hold-secondary">
                  {holdSecondary}
                </div>
              ) : null}
            </div>
            {historyToggle}
          </div>
          <div className="table-sheet-row__status table-sheet-row__status--hold">
            {tableHoldStatusLabel(t)}
          </div>
        </div>
        {historyPanel}
      </div>
    );
  }

  if (status === "booked") {
    const detail = tableRowLabel(table, t);
    const amount = formatBookingAmountDisplay(booking);
    const staffTitle = staffReferralTitle(booking);

    return (
      <div className="table-sheet-row-block">
        <div className="table-sheet-row table-sheet-row--booked">
          <TableCodeCell code={code} checkedIn={checkedIn} />
          <div className="table-sheet-row__detail">
            <span
              className="table-sheet-row__detail-text"
              title={staffTitle || undefined}
            >
              {detail}
            </span>
            {showNew ? <RowNewBadge /> : null}
            {historyToggle}
          </div>
          {amount ? (
            <div className="table-sheet-row__status table-sheet-row__status--amount">
              {amount}
            </div>
          ) : (
            <div className="table-sheet-row__status" />
          )}
        </div>
        {historyPanel}
      </div>
    );
  }

  if (status === "sold_out" || isTableOfflineSoldOut(table)) {
    return (
      <div className="table-sheet-row-block">
        <div className="table-sheet-row table-sheet-row--sold-out">
          <TableCodeCell code={code} checkedIn={false} />
          <div className="table-sheet-row__detail table-sheet-row__detail--dash">
            —
            {historyToggle}
          </div>
          <div className="table-sheet-row__status table-sheet-row__status--sold-out">
            {t("TABLE_SHEET_SOLD_OUT")}
          </div>
        </div>
        {historyPanel}
      </div>
    );
  }

  if (status === "unavailable") {
    return (
      <div className="table-sheet-row-block">
        <div className="table-sheet-row table-sheet-row--unavailable">
          <TableCodeCell code={code} checkedIn={false} />
          <div className="table-sheet-row__detail table-sheet-row__detail--dash">
            —
            {historyToggle}
          </div>
          <div className="table-sheet-row__status table-sheet-row__status--unavailable">
            {t("TABLE_SHEET_UNAVAILABLE")}
          </div>
        </div>
        {historyPanel}
      </div>
    );
  }

  // Only explicit available (or missing status) is bookable / shown as Available.
  if (status === "available" || !status) {
    return (
      <div className="table-sheet-row-block">
        <div className="table-sheet-row table-sheet-row--available">
          <TableCodeCell code={code} checkedIn={false} />
          <div className="table-sheet-row__detail table-sheet-row__detail--dash">
            —
            {historyToggle}
          </div>
          <div className="table-sheet-row__status table-sheet-row__status--available">
            {t("TABLE_SHEET_AVAILABLE")}
          </div>
        </div>
        {historyPanel}
      </div>
    );
  }

  return (
    <div className="table-sheet-row-block">
      <div className="table-sheet-row table-sheet-row--unavailable">
        <TableCodeCell code={code} checkedIn={false} />
        <div className="table-sheet-row__detail table-sheet-row__detail--dash">
          —
          {historyToggle}
        </div>
        <div className="table-sheet-row__status table-sheet-row__status--unavailable">
          {String(table.status || "").trim()}
        </div>
      </div>
      {historyPanel}
    </div>
  );
}

function TableSheetEventMenu({ events, selectedEventId, onSelect, onClose, t }) {
  return (
    <div className="table-sheet-event-menu" role="presentation">
      <button
        type="button"
        className="table-sheet-event-menu__backdrop"
        aria-label={t("TABLE_SHEET_CLOSE_EVENT_MENU")}
        onClick={onClose}
      />
      <div className="table-sheet-event-menu__panel" role="menu">
        {events.map((ev) => {
          const active = String(ev.eventId) === String(selectedEventId);
          const chipDate = formatEventChipDate(ev);
          return (
            <button
              key={ev.eventId}
              type="button"
              role="menuitem"
              className={`table-sheet-event-menu__item ${active ? "is-active" : ""}`}
              onClick={() => {
                onSelect(ev.eventId);
                onClose();
              }}
            >
              <span className="table-sheet-event-menu__title">{ev.title}</span>
              <span className="table-sheet-event-menu__meta">
                {chipDate ? `${chipDate} · ` : ""}
                <span
                  className={`table-sheet-event-badge ${eventStatusBadgeClass(
                    ev.eventstatus
                  )}`}
                >
                  {ev.eventstatus}
                </span>
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

const PublicTableSheetContainer = () => {
  const { t } = useTranslation();
  const { slug: rawSlug } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const slug = String(rawSlug || "").trim().toLowerCase();
  const eventIdFromUrl = searchParams.get("eventId")?.trim() || "";

  const [data, setData] = useState(null);
  const [loadState, setLoadState] = useState("loading");
  const [errorKind, setErrorKind] = useState(null);
  const [eventMenuOpen, setEventMenuOpen] = useState(false);
  const initialLoadDone = useRef(false);
  /** Seen booking IDs for this page session — set on load/refresh, unchanged during poll. */
  const seenBookingIdsRef = useRef([]);
  const loadInFlightRef = useRef(false);
  const lastGeneratedAtRef = useRef("");
  const fastUntilRef = useRef(0);
  const latestSheetRef = useRef(null);

  const beginSession = useCallback(() => {
    initialLoadDone.current = false;
    loadInFlightRef.current = false;
    lastGeneratedAtRef.current = "";
    fastUntilRef.current = 0;
    latestSheetRef.current = null;
    const storageKey = getTableSheetSeenStorageKey(slug, eventIdFromUrl);
    seenBookingIdsRef.current = readSeenBookingIds(storageKey);
  }, [slug, eventIdFromUrl]);

  const load = useCallback(
    async (opts = {}) => {
      const { silent = false } = opts;
      if (!slug) {
        setLoadState("error");
        setErrorKind("not-found");
        return null;
      }
      if (silent && loadInFlightRef.current) return null;
      if (!silent && !initialLoadDone.current) {
        setLoadState("loading");
      }
      if (silent) loadInFlightRef.current = true;
      try {
        const params = {};
        if (eventIdFromUrl) params.eventId = eventIdFromUrl;
        const seen = seenBookingIdsRef.current;
        if (seen.length > 0) {
          params.seenBookingIds = seen.join(",");
        }
        const raw = await fetchPublicTableSheet(slug, params);
        const parsed = unwrapTableSheetResponse(raw);
        if (!parsed?.club) {
          setData(null);
          setLoadState("error");
          setErrorKind("not-found");
          return null;
        }
        const nextGeneratedAt = String(parsed.sheet?.generatedAt || "");
        if (
          nextGeneratedAt &&
          lastGeneratedAtRef.current &&
          nextGeneratedAt !== lastGeneratedAtRef.current
        ) {
          fastUntilRef.current = Date.now() + POLL_BURST_MS;
        }
        if (nextGeneratedAt) lastGeneratedAtRef.current = nextGeneratedAt;
        latestSheetRef.current = parsed.sheet ?? null;

        setData(parsed);
        setErrorKind(null);
        setLoadState(!parsed.events?.length ? "empty-events" : "ok");
        initialLoadDone.current = true;

        if (!silent) {
          const resolvedEventId =
            parsed.selectedEventId ||
            eventIdFromUrl ||
            parsed.sheet?.event?.eventId ||
            "";
          const saveKey = getTableSheetSeenStorageKey(slug, resolvedEventId);
          writeSeenBookingIds(saveKey, parsed.sheet?.bookingIds);
        }
        return parsed;
      } catch (err) {
        if (!silent || !initialLoadDone.current) {
          setData(null);
          setLoadState("error");
          setErrorKind(err?.status === 404 ? "not-found" : "network");
        }
        return null;
      } finally {
        if (silent) loadInFlightRef.current = false;
      }
    },
    [slug, eventIdFromUrl]
  );

  useEffect(() => {
    beginSession();
    setData(null);
    setLoadState("loading");
    load();
  }, [beginSession, load]);

  useEffect(() => {
    let cancelled = false;
    let timeoutId = 0;

    const pollDelayFor = () => {
      if (Date.now() < fastUntilRef.current) return POLL_ACTIVE_MS;
      if (sheetNeedsFastPoll(latestSheetRef.current)) return POLL_ACTIVE_MS;
      return POLL_IDLE_MS;
    };

    const scheduleNext = (delayMs) => {
      window.clearTimeout(timeoutId);
      timeoutId = window.setTimeout(runPoll, delayMs);
    };

    const runPoll = async () => {
      if (cancelled) return;
      if (document.visibilityState !== "visible") {
        scheduleNext(POLL_IDLE_MS);
        return;
      }
      await load({ silent: true });
      if (cancelled) return;
      scheduleNext(pollDelayFor());
    };

    const onVisibility = () => {
      if (document.visibilityState !== "visible") return;
      fastUntilRef.current = Date.now() + POLL_BURST_MS;
      window.clearTimeout(timeoutId);
      runPoll();
    };

    scheduleNext(POLL_ACTIVE_MS);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [load]);

  const onSelectEvent = useCallback(
    (eventId) => {
      const id = String(eventId || "").trim();
      if (!id || !slug) return;
      navigate(`/clubs/${slug}/table-sheet?eventId=${encodeURIComponent(id)}`, {
        replace: true,
      });
    },
    [navigate, slug]
  );

  const sheet = data?.sheet;
  const events = data?.events ?? [];
  const selectedEventId =
    eventIdFromUrl || data?.selectedEventId || sheet?.event?.eventId || "";

  const staffIndex = useMemo(
    () => buildSheetStaffNameIndex(sheet),
    [sheet]
  );

  const topbarEventTitle = useMemo(() => {
    const ev = events.find((e) => String(e.eventId) === String(selectedEventId));
    if (ev?.title) return ev.title;
    return sheet?.event?.title || "";
  }, [events, selectedEventId, sheet?.event?.title]);

  const topbarEventDate = useMemo(() => {
    const ev = events.find((e) => String(e.eventId) === String(selectedEventId));
    const fromList = formatEventStartDateDisplay(ev);
    if (fromList) return fromList;
    return formatEventStartDateDisplay(sheet?.event);
  }, [events, selectedEventId, sheet?.event]);

  const cloudinaryBase = getCloudinaryMediaBaseUrl();
  const logoRaw = data?.club?.logo ? String(data.club.logo).trim() : "";
  const clubLogoSrc = useMemo(() => {
    if (!logoRaw) return "";
    if (/^https?:\/\//i.test(logoRaw) || logoRaw.startsWith("data:")) {
      return logoRaw;
    }
    return resolveCloudinaryImageSrc(logoRaw, cloudinaryBase, "");
  }, [logoRaw, cloudinaryBase]);

  const pageTitle = sheet?.event?.title
    ? `${sheet.event.title} · ${t("TABLE_SHEET_TITLE")}`
    : t("TABLE_SHEET_TITLE");

  if (loadState === "loading") {
    return (
      <div className="table-sheet-page table-sheet-page--loading min-vh-100 d-flex align-items-center justify-content-center">
        <Helmet>
          <title>{t("TABLE_SHEET_TITLE")}</title>
          <meta name="robots" content="noindex, nofollow" />
        </Helmet>
        <CustomLoadingHTML />
      </div>
    );
  }

  if (loadState === "error") {
    return (
      <div className="table-sheet-page min-vh-100 d-flex flex-column align-items-center justify-content-center px-3">
        <Helmet>
          <title>{t("TABLE_SHEET_TITLE")}</title>
          <meta name="robots" content="noindex, nofollow" />
        </Helmet>
        <p className="table-sheet-error-msg mb-3">
          {errorKind === "not-found"
            ? t("TABLE_SHEET_CLUB_NOT_FOUND")
            : t("TABLE_SHEET_LOAD_ERROR")}
        </p>
        {errorKind === "network" ? (
          <button
            type="button"
            className="btn btn-outline-light btn-sm"
            onClick={() => {
              beginSession();
              load();
            }}
          >
            {t("TABLE_SHEET_RETRY")}
          </button>
        ) : null}
      </div>
    );
  }

  if (loadState === "empty-events") {
    return (
      <div className="table-sheet-page px-3 py-4">
        <Helmet>
          <title>{pageTitle}</title>
          <meta name="robots" content="noindex, nofollow" />
        </Helmet>
        <div className="table-sheet-topbar">
          <span className="table-sheet-live-pill">{t("TABLE_SHEET_LIVE")}</span>
          <div className="table-sheet-topbar__center" />
          <span />
        </div>
        <div className="table-sheet-hero-block">
          <h1 className="table-sheet-hero-title">{t("TABLE_SHEET_TITLE")}</h1>
        </div>
        <div className="table-sheet-empty py-5">{t("TABLE_SHEET_NO_EVENTS")}</div>
      </div>
    );
  }

  return (
    <div className="table-sheet-page px-3 py-3">
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div className="table-sheet-topbar">
        <span className="table-sheet-live-pill">{t("TABLE_SHEET_LIVE")}</span>
        <div className="table-sheet-topbar__center">
          <p className="table-sheet-topbar__sheet-label">{t("TABLE_SHEET_TITLE")}</p>
        </div>
        {events.length > 1 ? (
          <button
            type="button"
            className="table-sheet-menu-btn"
            aria-label={t("TABLE_SHEET_EVENT_SELECTOR")}
            onClick={() => setEventMenuOpen(true)}
          >
            <span />
            <span />
            <span />
          </button>
        ) : (
          <span />
        )}
      </div>

      <div className="table-sheet-hero-block">
        {topbarEventTitle ? (
          <h1 className="table-sheet-hero-title">{topbarEventTitle}</h1>
        ) : null}
        {topbarEventDate ? (
          <p className="table-sheet-hero-date">{topbarEventDate}</p>
        ) : null}
        {clubLogoSrc ? (
          <div className="table-sheet-club-logo-wrap">
            <img
              src={clubLogoSrc}
              alt={data?.club?.name || ""}
              className="table-sheet-club-logo"
            />
          </div>
        ) : null}
      </div>

      <div className="table-sheet-content-wrap">
        <div className="table-sheet-sections">
          {(sheet?.sections ?? []).map((section) => (
            <section
              key={section.sectionId}
              className={`table-sheet-section ${sectionColorClass(
                section.sectionColor
              )}`}
            >
              <h3 className="table-sheet-section__title">
                <SectionSofaIcon />
                {sectionHeaderLabel(section)}
              </h3>
              <div className="table-sheet-section__body">
                {(section.tables ?? []).map((table) => (
                  <TableSheetTableRow
                    key={table.tableId}
                    table={table}
                    staffIndex={staffIndex}
                  />
                ))}
              </div>
            </section>
          ))}
        </div>
      </div>

      {sheet?.generatedAt ? (
        <footer className="table-sheet-footer">
          <span className="table-sheet-footer__dot" aria-hidden />
          {t("TABLE_SHEET_UPDATED", {
            time: formatGeneratedAt(sheet.generatedAt),
          })}
        </footer>
      ) : null}

      {eventMenuOpen && events.length > 1 ? (
        <TableSheetEventMenu
          events={events}
          selectedEventId={selectedEventId}
          onSelect={onSelectEvent}
          onClose={() => setEventMenuOpen(false)}
          t={t}
        />
      ) : null}
    </div>
  );
};

export default PublicTableSheetContainer;
